import React, { useEffect, useState } from 'react';
import { Save, Loader2, Upload } from 'lucide-react';
import { fetchSettings, updateSettings } from '../../utils/adminApi';
import { uploadImage, resolveImage } from '../../utils/imageHelper';
import { BONUS_ICONS, WITHDRAW_ICONS } from '../../utils/icons';

const SettingsPage = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    fetchSettings().then(setData).catch(() => {});
  }, []);

  const on = (key) => (e) => setData({ ...data, [key]: e.target.value });
  const set = (key, val) => setData({ ...data, [key]: val });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setErr('');
    try {
      const saved = await updateSettings(data);
      setData(saved);
      setMsg('Settings updated successfully.');
    } catch (e) {
      setErr('Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  const onLogoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await uploadImage(f);
      set('logoUrl', url);
    } catch (err) {
      alert('Upload failed. Please try again.');
    }
  };

  if (!data) return <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  const bonusIcon = data.bonusIcon || 'PartyPopper';
  const withdrawIcon = data.withdrawIcon || 'Wallet';

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-4">Site Settings</h1>
      {msg && <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">{msg}</div>}
      {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}
      <form onSubmit={submit} className="space-y-4">
        {/* Logo */}
        <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Site Logo (Home Page Header)</h2>
          <div className="flex items-center gap-4">
            {data.logoUrl ? (
              <img src={resolveImage(data.logoUrl)} alt="logo" className="w-16 h-16 rounded-full object-cover border border-gray-300" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-yellow-300 border border-yellow-400 flex items-center justify-center text-[9px] font-bold text-red-700 text-center">GOGO YONO GAMES</div>
            )}
            <div className="flex-1 space-y-2">
              <input
                type="text"
                value={data.logoUrl || ''}
                onChange={on('logoUrl')}
                placeholder="Logo URL (leave blank for text logo)"
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex gap-2">
                <label className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2271b1] hover:text-[#135e96] cursor-pointer">
                  <Upload className="w-3.5 h-3.5" /> Upload logo from device
                  <input type="file" accept="image/*" className="hidden" onChange={onLogoUpload} />
                </label>
                {data.logoUrl && (
                  <button type="button" onClick={() => set('logoUrl', '')} className="text-xs text-red-600 hover:underline">Remove logo</button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Icon pickers */}
        <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Bonus & Withdraw Icons</h2>

          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-2">Welcome Bonus Icon</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(BONUS_ICONS).map(([name, Icon]) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => set('bonusIcon', name)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 border rounded text-sm transition-colors ${
                    bonusIcon === name ? 'bg-[#2271b1] text-white border-[#2271b1]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${bonusIcon === name ? '' : 'text-[#c62828]'}`} />
                  {name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Min Withdraw Icon</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(WITHDRAW_ICONS).map(([name, Icon]) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => set('withdrawIcon', name)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 border rounded text-sm transition-colors ${
                    withdrawIcon === name ? 'bg-[#2271b1] text-white border-[#2271b1]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${withdrawIcon === name ? '' : 'text-[#0f8a4a]'}`} />
                  {name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* General */}
        <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3">General</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Site Title" value={data.siteTitle} onChange={on('siteTitle')} />
            <Field label="Site Tagline" value={data.siteTagline} onChange={on('siteTagline')} />
            <Field label="Telegram Link" value={data.telegramLink} onChange={on('telegramLink')} placeholder="https://t.me/yourchannel" />
            <Field label="Footer Text" value={data.footerText} onChange={on('footerText')} />
          </div>
        </div>

        {/* SEO */}
        <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3">SEO Meta (Home Page)</h2>
          <div className="grid grid-cols-1 gap-3">
            <Field label="Meta Title" value={data.metaTitle} onChange={on('metaTitle')} />
            <TextArea label="Meta Description" value={data.metaDescription} onChange={on('metaDescription')} rows={3} />
            <Field label="Meta Keywords (comma-separated)" value={data.metaKeywords} onChange={on('metaKeywords')} />
          </div>
        </div>

        {/* Home content */}
        <div className="bg-white border border-gray-200 rounded shadow-sm p-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Home Intro Content</h2>
          <div className="grid grid-cols-1 gap-3">
            <Field label="Intro Title" value={data.homeIntroTitle} onChange={on('homeIntroTitle')} />
            <TextArea label="Intro Paragraph (leave blank to use default)" value={data.homeIntroContent || ''} onChange={on('homeIntroContent')} rows={5} />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#2271b1] hover:bg-[#135e96] text-white text-sm font-medium px-4 py-2 rounded transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Settings
        </button>
      </form>
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <input value={value || ''} onChange={onChange} placeholder={placeholder} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 3 }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <textarea value={value || ''} onChange={onChange} rows={rows} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
  </div>
);

export default SettingsPage;
